/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Measure from 'react-measure';
import { classnames as cx, propsUtils, bidiUtils, Bidi } from '@ba-ui-toolkit/utils';
import Tooltip from '@ba-ui-toolkit/tooltip';

import styles from './TruncatedText.scss';

const truncatedTextClassName = styles.truncatedTextClassName.replace(/"/g, '');
const CHAR_THRESHOLD = 4;
const { sanitize } = propsUtils;
const { resolveBaseTextDir } = bidiUtils;

export default class TruncatedText extends Component {
	static propTypes = {
		value: PropTypes.string.isRequired,
		location: PropTypes.oneOf(['end', 'middle']),
		textAlign: PropTypes.oneOf(['left', 'right', 'center']),
		title: PropTypes.string,
		className: PropTypes.string,
		textDir: PropTypes.oneOf(['', 'ltr', 'rtl', 'auto']),
		numShape: PropTypes.bool,
		style: PropTypes.object,
		disabled: PropTypes.bool,
		allowTooltip: PropTypes.bool,
		tooltipDelay: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
	};

	static defaultProps = {
		location: 'middle',
		className: '',
		style: {},
		allowTooltip: true,
	};

	state = {
		charCount: this._handleValueType().length,
	};

	componentDidMount() {
		// Defect 234641: Added._textNode.offsetWidth condition check to prevent truncated text with 0 max width
		if (!this._textNode.offsetWidth) {
			setTimeout(this._getTextWidths);
		} else {
			this._textNode && this._getTextWidths();
		}
	}

	componentWillReceiveProps(nextProps) {
		if (nextProps.value !== this.props.value) {
			// reset charCount to trigger measuring again
			const nextValueProp = this._handleValueType(nextProps.value);
			this.setState({ charCount: nextValueProp.length, _isMounted: false });
		}
	}

	componentDidUpdate(prevProps, prevState) {
		if (this.state._isMounted) {
			const containerWidth = this._node.offsetWidth;
			this._textWidth = this._textNode.offsetWidth;

			if (prevState._isMounted !== this.state._isMounted) {
				// check if resizing is still needed after first render
				this._resizeText(containerWidth);
			} else if (prevState.charCount !== this.state.charCount) {
				// keep track of position when last truncation was made
				this._lastTruncPos = containerWidth;
				if (containerWidth < this._textWidth) {
					// keep resizing until textWidth is < container
					this._shortenText(containerWidth);
				}
			}
		} else if (prevProps.value !== this.props.value) {
			// trigger measuring after updating props (i.e. in Select)
			this._getTextWidths();
		}
	}

	onMeasure = dimensions => {
		if (this.state._isMounted) {
			const containerWidth = Math.ceil(dimensions.entry.width);
			this._resizeText(containerWidth);
		} else {
			this._getTextWidths();
		}
	};

	_handleValueType(value = this.props.value) {
		return value == null ? '' : String(value);
	}

	_getTextWidths = () => {
		const value = this._handleValueType();
		// get initial widths and char counts
		if (this._textNode) {
			this._textWidth = this._textNode.offsetWidth;
			this._initTextWidth = this._textNode.offsetWidth;
			this._initCharCount = value.length;
			this._initCharWidth = this._getCharWidth(value.length);
			this._lastTruncPos = this._initTextWidth;
			this.setState({ _isMounted: true });
		}
	};

	_resizeText(containerWidth) {
		if (containerWidth < this._textWidth && containerWidth < this._lastTruncPos) {
			this._shortenText(containerWidth);
		} else if (
			containerWidth > this._textWidth &&
			containerWidth < this._initTextWidth &&
			containerWidth > this._lastTruncPos
		) {
			this._lengthenText(containerWidth);
		} else if (
			containerWidth >= this._initTextWidth &&
			this.state.charCount !== this._initCharCount
		) {
			// show entire string by resetting charCount
			this._lastTruncPos = this._initTextWidth;
			this.setState({ charCount: this._initCharCount });
		}
	}

	_shortenText(containerWidth) {
		const { charCount } = this.state;
		if (charCount > 0 && containerWidth > 0) {
			const charWidth = this._getCharWidth(charCount);
			const extraChars = this._getExcessChars(containerWidth, charWidth);
			const newCharCount = Math.max(0, charCount - Math.max(1, extraChars));
			this.setState({ charCount: newCharCount });
		}
	}

	_lengthenText(containerWidth) {
		const value = this._handleValueType();
		const { charCount } = this.state;
		if (charCount < value.length) {
			const charWidth = this._getCharWidth(charCount);
			const diff = containerWidth - this._textWidth;
			const missingChars = this._getExcessChars(containerWidth, charWidth);
			if (diff > 1.5 * charWidth || value.length - charCount === 1) {
				const newCharCount = Math.min(value.length, charCount + missingChars);
				if (newCharCount !== charCount) {
					this.setState({ charCount: newCharCount });
				}
			}
		}
	}

	_getCharWidth(charCount) {
		return charCount > CHAR_THRESHOLD ? this._textWidth / charCount : this._initCharWidth;
	}

	_getExcessChars(containerWidth, charWidth) {
		const extraChars = Math.floor((this._textWidth - containerWidth) / charWidth);

		return Math.abs(extraChars);
	}

	_ref = node => {
		this._node = node;
		this._measureRef(node);
	};

	_refText = node => {
		this._textNode = node;
	};

	render() {
		const {
			title,
			className,
			textAlign,
			style,
			textDir,
			disabled,
			allowTooltip,
			tooltipDelay,
			...others
		} = this.props;
		const value = this._handleValueType();
		const { charCount, _isMounted } = this.state;

		const initCharCount = value.length;

		let ellipsisValue = Bidi.reshape(value, this.props.numShape);

		const isEllipsed = charCount >= 0 && charCount < initCharCount;

		if (isEllipsed) {
			if (charCount <= 1) {
				ellipsisValue = '\u2026';
			} else {
				const charStart = Math.floor(charCount / 2);
				const charEnd = initCharCount - Math.ceil(charCount / 2) + 1;
				ellipsisValue =
					this.props.location === 'middle'
						? `${value.substring(0, charStart)}\u2026${value.substring(charEnd)}`
						: `${value.substring(0, charCount - 3)}\u2026`;
			}
		}

		const containerStyle = {
			textAlign,
			// Defect 226704: Added the fallback inline styles
			// Defect 234641: Added _initTextWidth as part of condition check
			maxWidth: _isMounted && this._initTextWidth !== 0 ? this._initTextWidth + 2 : '100%',
			display: _isMounted ? 'table' : 'block',
			// visibility: _isMounted ? 'visible' : 'hidden',
			...style,
		};

		const titleOrLabel = title || (isEllipsed && value);
		const ariaLabel = title || value;

		const textNode = (
			<div
				data-tid="ba-common-truncated-text__text-node"
				className={`${cx(`${truncatedTextClassName}__text`)}`}
				ref={this._refText}
				aria-label={ariaLabel}
				dir={resolveBaseTextDir(ellipsisValue, textDir, false)}
			>
				{ellipsisValue}
			</div>
		);

		const toolTipNode = (
			<Tooltip
				title={titleOrLabel}
				delay={tooltipDelay}
				className={`${truncatedTextClassName}__tooltip`}
				orient="top"
				textDir={textDir}
			>
				{textNode}
			</Tooltip>
		);

		const classNames = cx(truncatedTextClassName, { disabled });

		return (
			<Measure onResize={this.onMeasure}>
				{({ measureRef }) => {
					this._measureRef = measureRef;

					return (
						<div
							{...sanitize(others, TruncatedText)}
							className={`${classNames} ${className}`}
							ref={this._ref}
							style={{ ...containerStyle }}
						>
							{titleOrLabel && allowTooltip ? toolTipNode : textNode}
						</div>
					);
				}}
			</Measure>
		);
	}
}
